import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import ModeToggle from '../components/ModeToggle';

export default function DailyQuestions() {
  const { API, mode, user } = useApp();
  const nav = useNavigate();
  const isCyber = mode === 'cyber';
  const [qs, setQs] = useState([]);
  const [ans, setAns] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!user) { nav('/auth'); return; }
    const token = localStorage.getItem('pp-token') || '';
    axios.get(`${API}/daily/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.data.completed) { setErr('You have already completed today. Come back tomorrow!'); return; }
        return axios.get(`${API}/daily/questions`, { headers: { Authorization: `Bearer ${token}` } });
      })
      .then(r => { if (r) setQs(r.data.questions); })
      .catch(() => nav('/auth'));
  }, [user]);

  const pick = (letter) => {
    const q = qs[idx];
    setAns(a => ({ ...a, [q.id]: letter }));
    setTimeout(() => { if (idx < qs.length - 1) setIdx(idx + 1); }, 180);
  };

  const submit = async () => {
    const token = localStorage.getItem('pp-token') || '';
    const r = await axios.post(`${API}/daily/submit`,
      { answers: Object.entries(ans).map(([question_id, a]) => ({ question_id, answer: a })) },
      { headers: { Authorization: `Bearer ${token}` } });
    setResult(r.data);
  };

  const cardCls = isCyber ? 'glass-cyber rounded-2xl p-8' : 'parchment rounded-2xl p-8';

  return (
    <div className={`min-h-screen px-6 py-10 ${isCyber ? 'bg-black text-white' : 'text-[#2C2621]'}`}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <button data-testid="daily-back" onClick={() => nav('/')} className={isCyber ? 'font-mono-cyber text-[#00FFD1]' : 'font-body-story'}>← back</button>
          <ModeToggle />
        </div>
        <h1 className={isCyber ? 'font-orbitron text-3xl mb-8' : 'font-storybook text-4xl mb-8'}>
          {isCyber ? 'DAILY QUEST' : 'Daily Quest of the Kingdom'}
        </h1>

        {err && <div className={cardCls}><div className="text-lg">{err}</div></div>}

        {!err && qs.length > 0 && !result && (() => {
          const q = qs[idx];
          return (
            <motion.div key={q.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className={cardCls}>
              <div className={isCyber ? 'font-mono-cyber text-[#00FFD1] text-xs mb-2' : 'font-body-story italic text-[#68615A] mb-2'}>
                Question {idx + 1} of {qs.length} · {q.subject}
              </div>
              <div className={`text-xl mb-6 ${isCyber ? 'font-orbitron' : 'font-storybook'}`}>{q.prompt}</div>
              <div className="grid gap-3">
                {Object.entries(q.choices).map(([letter, text]) => (
                  <button key={letter} data-testid={`daily-choice-${letter}`} onClick={() => pick(letter)}
                    className={`text-left px-5 py-4 rounded-xl border-2 ${
                      ans[q.id] === letter
                        ? isCyber ? 'border-[#00FFD1] bg-[#00FFD1]/10' : 'border-[#8C1C13] bg-[#8C1C13]/10'
                        : isCyber ? 'border-neutral-800 hover:border-[#00FFD1]/40' : 'border-[#C9B99A] hover:border-[#8C1C13]/40'
                    }`}>
                    <span className={`inline-block w-7 mr-2 font-bold ${isCyber ? 'text-[#00FFD1] font-mono-cyber' : 'text-[#8C1C13] font-storybook'}`}>{letter}.</span>{text}
                  </button>
                ))}
              </div>
              {idx === qs.length - 1 && ans[q.id] && (
                <button data-testid="daily-submit" onClick={submit}
                  className={`mt-6 px-6 py-3 rounded-lg ${isCyber ? 'bg-[#00FFD1] text-black font-orbitron tracking-widest' : 'storybook-btn'}`}>
                  {isCyber ? 'SUBMIT →' : 'Present to the Court'}
                </button>
              )}
            </motion.div>
          );
        })()}

        {result && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={cardCls}>
            <div className={isCyber ? 'font-mono-cyber text-[#00FFD1] mb-2' : 'font-body-story italic text-[#68615A] mb-2'}>Daily quest complete</div>
            <div className={isCyber ? 'font-orbitron text-5xl text-[#00FFD1]' : 'font-storybook text-5xl text-[#8C1C13]'}>{result.score} / {result.total}</div>
            <div className="mt-4">Return tomorrow for a fresh challenge.</div>
            <button data-testid="daily-home" onClick={() => nav('/')} className={`mt-4 px-5 py-2 rounded-lg ${isCyber ? 'border border-[#00FFD1]/40 text-[#00FFD1] font-orbitron' : 'storybook-btn'}`}>Home</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
